import { StripeProduct } from './../../_models/StripeProduct';
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { PaymentService } from '../../_services/payment.service';
import { StripePrice } from '../../_models/StripePrice';
import { Router } from '@angular/router';
import { CheckoutDataKey, UpdateSubscriptionDataKey } from '../../constants';
import { CheckoutModel } from '../../_models/CheckoutModel';
import { Subject } from 'rxjs';
import { StripeSubscription } from '../../_models/StripeSubscription';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { StripeService } from 'ngx-stripe';
import { ProductType } from 'on-common/_models/ProducType';

marker('day');
marker('week');
marker('month');
marker('year');

@Component({
    selector: 'on-stripe-payment',
    templateUrl: './stripe-payment.component.html',
    styleUrls: ['./stripe-payment.component.scss'],
})
export class StripePaymentComponent implements OnInit, OnDestroy {
    @Input() currentSubscription: StripeSubscription;

    products: StripeProduct[];
    selectedProduct: StripeProduct;
    selectedPrice: StripePrice;
    quantity = 1;
    totalPrice: number;

    destroy$ = new Subject<void>();

    isLoading: boolean;

    constructor(
        private paymentService: PaymentService,
        private router: Router,
    ) {}

    ngOnInit() {
        this.isLoading = true;
        this.paymentService.GetAllProducts().subscribe((response) => {
            if (response.IsSuccess) {
                // Set products and order them from lower price to upper price
                this.products = response.Result.filter((p) => p.IsLicense).sort(
                    (a, b) => {
                        const monthPriceA = a.Prices.find(
                            (p) => p.Interval === 'month',
                        );
                        const monthPriceB = b.Prices.find(
                            (p) => p.Interval === 'month',
                        );
                        if (monthPriceA?.Price > monthPriceB?.Price) {
                            return 1;
                        } else {
                            return -1;
                        }
                    },
                );
                if (!this.currentSubscription) {
                    this.selectedProduct = null;
                } else {
                    this.quantity = this.currentSubscription.Quantity;

                    // Find the product containing the current price (backend injects legacy prices with IsCurrentPrice flag)
                    this.selectedProduct = this.products.find(
                        (p) =>
                            p.Prices.find((price) => price.IsCurrentPrice) !=
                            null,
                    );

                    if (this.selectedProduct) {
                        this.selectedPrice = this.selectedProduct.Prices.find(
                            (price) => price.IsCurrentPrice,
                        );
                        const selectedProductIndex = this.products.indexOf(
                            this.selectedProduct,
                        );
                        // Set products that are lower price to disabled
                        for (let i = 0; i < this.products.length; i++) {
                            if (i < selectedProductIndex) {
                                this.products[i].disabled = true;
                            } else {
                                break;
                            }
                        }
                    }
                }
            }
            this.isLoading = false;
        });
    }

    ngOnDestroy() {
        if (this.destroy$) {
            this.destroy$.next();
            this.destroy$.complete();
        }
    }

    updateInfoData() {
        if (!this.selectedPrice) {
            return;
        }
        this.totalPrice = this.selectedPrice.Price * this.quantity;
        if (this.selectedPrice.IsFloating) {
            this.quantity = 1;
        }
    }

    buy() {
        if (!this.selectedPrice || !this.selectedProduct || !this.quantity) {
            return;
        }

        const checkoutData: CheckoutModel = {
            product: this.selectedProduct,
            price: this.selectedPrice,
            nbOfLicenses: this.quantity,
        };

        sessionStorage.setItem(CheckoutDataKey, JSON.stringify(checkoutData));

        this.router.navigate(['/subscription', 'checkout']);
    }

    changeSubscription() {
        if (
            !this.currentSubscription ||
            (this.currentSubscription.Quantity === this.quantity &&
                this.currentSubscription.RecurringPrice.Id ===
                    this.selectedPrice.Id)
        ) {
            return;
        }

        const checkoutData: CheckoutModel = {
            product: this.selectedProduct,
            price: this.selectedPrice,
            nbOfLicenses: this.quantity,
            originalNbOfLicenses: this.currentSubscription.Quantity,
            originalPrice: this.currentSubscription.RecurringPrice,
            originalProduct: this.currentSubscription.RecurringPrice.Product,
            originalRenewingDate: this.currentSubscription.NextRenewalDate,
        };

        sessionStorage.setItem(
            UpdateSubscriptionDataKey,
            JSON.stringify(checkoutData),
        );

        this.router.navigate(['/subscription', 'update']);
    }

    selectProduct(product: StripeProduct) {
        if (this.selectedProduct === product || product.disabled) {
            return;
        }
        this.selectedProduct = product;
        if (!this.currentSubscription) {
            this.selectedPrice = null;
        } else {
            // Try to find the current subscription price in this product
            const currentPrice = this.selectedProduct.Prices.find(
                (p) => p.IsCurrentPrice,
            );

            if (currentPrice) {
                this.selectedPrice = currentPrice;
            } else {
                // Different product: try to find a price with the same interval
                this.selectedPrice = this.selectedProduct.Prices.find(
                    (p) =>
                        p.Interval === this.selectedPrice.Interval &&
                        p.IntervalCount === this.selectedPrice.IntervalCount,
                );

                // Otherwise, take the first price available
                if (!this.selectedPrice) {
                    this.selectedPrice = this.selectedProduct.Prices[0];
                }
            }
        }
        this.updateInfoData();
    }

    selectPrice() {
        this.updateInfoData();
    }

    quantityChanged(newQuantity: string) {
        this.quantity = +newQuantity;
        this.updateInfoData();
    }
}
